import '@mux/mux-player';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { LRS_ACTIONS } from '../../../_shared';

import { MUX_PLAYER_STREAM_TYPE } from '../../constants';

const REGEX_ASPECT_RATIO = /(?<width>\d*)[x|by]*(?<height>\d*)/;

const propTypes = {

	/**
	 * The video id.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The title of the video. Used for metrics in Mux Data.
	 */
	title: PropTypes.string,

	/**
	 * The content type containing the video (e.g. event, resource, discussion thread).
	 * Used for metrics in Mux Data.
	 */
	contentType: PropTypes.string,

	/**
	 * The stream type for the video. The video
	 * player controls will change depending on
	 * the stream type.
	 *
	 * https://docs.mux.com/guides/player/play-your-assets#adaptive-controls
	 */
	streamType: PropTypes.oneOf([
		MUX_PLAYER_STREAM_TYPE.LIVE,
		MUX_PLAYER_STREAM_TYPE.LIVE_DVR,
		MUX_PLAYER_STREAM_TYPE.LOW_LATENCY_LIVE,
		MUX_PLAYER_STREAM_TYPE.LOW_LATENCY_LIVE_DVR,
		MUX_PLAYER_STREAM_TYPE.ON_DEMAND
	]).isRequired,

	/**
	 * The Mux signed playback ID for the video.
	 */
	playbackId: PropTypes.string.isRequired,

	/**
	 * The JWTs to send with the request for videos
	 * with signed playback policies. This is required
	 * as all Mux videos on the platform should have a
	 * signed playback policy.
	 */
	tokens: PropTypes.shape({

		/**
		 * The token to authorize video playback.
		 */
		video: PropTypes.string.isRequired,

		/**
		 * The token to authorize loading the thumbnail.
		 */
		thumbnail: PropTypes.string.isRequired,

		/**
		 * The token to authorize loading the storyboard.
		 */
		storyboard: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The domain of the organization for the video. Used for metrics in Mux Data.
	 * Mux Data metric is called "Sub Property ID"
	 */
	orgDomain: PropTypes.string,

	/**
	 * The user ID of the user currently viewing the video, if any.
	 * Used for metrics in Mux Data.
	 */
	viewerUserId: PropTypes.string,

	/**
	 * The aspect ratio for the video.
	 */
	aspectRatio: PropTypes.string.isRequired,

	/**
	 * Flag to auto play the video.
	 */
	autoplay: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.bool,
	]),

	/**
	 * Callback function to handle video watching.
	 */
	onVideoWatch: PropTypes.func,

};

/**
 * The video event types to listen to.
 *
 * eventType - The type of event emitted by the event handler
 * status - The "action" to report to the LRS.
 */
const VIDEO_EVENTS = [
	{
		eventType: 'play',
		status: LRS_ACTIONS.PLAYED,
	},
	{
		eventType: 'pause',
		status: LRS_ACTIONS.PAUSED,
	},
	{
		eventType: 'ended',
		status: LRS_ACTIONS.FINISHED,
	},
];


/**
 * Displays the Mux video player for a given Mux playback ID.
 * Depending on the stream type, different controls and features will be
 * displayed on the player.
 *
 * Metadata contains the video title and current viewer's user ID
 * which is reported to Mux Data.
 *
 * Options for the player include aspect ratio and autoplay
 *
 * Reports played, paused, and ended video events to the onVideoWatch
 * function, if provided. Reported data includes the status (played, paused,
 * ended), the currentTime, and the totalTime (duration).
 *
 * Documentation for customization can be found here:
 * https://docs.mux.com/guides/player/customize-look-and-feel
 *
 * @param {Object} props  The component props.
 * @returns {Object}      The MuxPlayer component.
 */
const MuxPlayer = (props) => {

	const {
		id,
		title,
		contentType,
		streamType,
		playbackId,
		tokens,
		orgDomain,
		viewerUserId,
		aspectRatio,
		autoplay,
		onVideoWatch,
	} = props;

	// Parse the aspect ratio for styling
	const { width, height } = REGEX_ASPECT_RATIO.exec(aspectRatio).groups;

	const style = {

		aspectRatio: `${width}/${height}`,

		// Set the players accent color to our primary color for branding
		'--media-accent-color': 'var(--primary-color)',

	};

	/**
	 * Listen for play, pause, and ended events to
	 * report via the onVideoWatch function, if provided
	 */
	useEffect(() => {

		if (!onVideoWatch) { return () => {}; }

		const muxPlayerElement = document.getElementById(id);

		const listenerFunction = (event) => {

			// Get LRS action from the event type
			const { status } = VIDEO_EVENTS.find((item) => item.eventType === event.type);

			// Get the latest attributes from the player
			const updatedMuxPlayer = document.getElementById(id);

			// Get the current timestamp of the video
			const currentTime = updatedMuxPlayer.currentTime;

			// Make sure duration is not infinite or NaN
			const totalTime = Number.isFinite(updatedMuxPlayer.duration)
				? updatedMuxPlayer.duration
				: null;

			// Send metadata to callback function
			onVideoWatch({ status, currentTime, totalTime });

		};

		VIDEO_EVENTS.forEach((listener) => {
			muxPlayerElement.addEventListener(listener.eventType, listenerFunction);
		});

		return () => {
			VIDEO_EVENTS.forEach((listener) => {
				muxPlayerElement.removeEventListener(listener.eventType, listenerFunction);
			});
		};

	}, [id, onVideoWatch]);

	const playerProps = {

		id,
		style,
		autoplay,
		['playback-id']: playbackId,

		// Set the stream type which renders different defaults in the player
		['stream-type']: streamType,

		// Set JWT tokens to load signed assets
		['playback-token']: tokens.video,
		['thumbnail-token']: tokens.thumbnail,
		['storyboard-token']: tokens.storyboard,

		// Set metadata for analysis in Mux Data
		['metadata-video-id']: id,
		['metadata-video-title']: title,
		['metadata-viewer-user-id']: viewerUserId,
		['metadata-sub-property-id']: orgDomain,
		['metadata-video-content-type']: contentType,

		// Disable captions by default
		['default-hidden-captions']: true,

	};

	return <mux-player {...playerProps} />;

};

MuxPlayer.propTypes = propTypes;

export default MuxPlayer;
